/**
 * Host + path glob matching for widget-scope rules.
 *
 * THE PATH GLOBS ARE A DELIBERATE COPY of `../scan-rules/matcher.ts:18-41`,
 * not an import. Scan rules decide what the crawler INDEXES; widget scope
 * decides what the widget SHOWS. Keeping a tenant's page out of the AI's
 * corpus while still running the widget on it — and the reverse — is the whole
 * reason this feature exists, so a dependency edge from widget-facing code
 * onto indexing would recreate exactly the coupling it was built to remove.
 * The two are free to diverge; the semantics are identical today on purpose,
 * so a tenant who has learned one pattern language already knows the other.
 *
 * NOT reused either: `widget/src/observers/url-match.ts`. Its trailing `*` is a
 * PREFIX match (`/blog/*` matches `/blog/x/y/z`), whereas `*` here is exactly
 * one segment. Extending that function to serve both would silently change
 * trigger scoping for every tenant already using it.
 */

const REGEX_SPECIALS = /[.+^${}()|[\]\\]/g;
const escapeRegex = (s: string): string => s.replace(REGEX_SPECIALS, "\\$&");

/**
 * Compile a path glob (segments split on '/'): a whole segment `*` is ONE
 * non-empty segment; `**` is zero or more segments, absorbing its leading
 * slash; `*` embedded in a segment is a non-slash run. `/ca/**` therefore
 * matches `/ca`, `/ca/`, and `/ca/anything/deeper`.
 */
function compilePathGlob(glob: string): RegExp {
  const norm = glob.startsWith("/") ? glob : `/${glob}`;
  const segments = norm.split("/").filter((s, i) => !(i === 0 && s === ""));
  const parts: string[] = [];
  for (const seg of segments) {
    if (seg === "**") {
      parts.push("(?:/[^/]+)*");
    } else if (seg === "*") {
      parts.push("/[^/]+");
    } else {
      parts.push("/" + escapeRegex(seg).replace(/\\?\*/g, "[^/]*"));
    }
  }
  // Allow a trailing slash on the path being tested.
  return new RegExp(`^${parts.join("")}/?$`, "i");
}

/**
 * Does this URL's PATH match the glob?
 *
 * Pathname only — query and hash are not consulted, matching how tenants think
 * about "pages". Never throws: a string that is not a parseable URL is treated
 * as a bare pathname, because a matcher that can throw is a matcher that can
 * take the widget down.
 */
export function matchPath(pattern: string, url: string): boolean {
  let path: string;
  try {
    path = new URL(url).pathname;
  } catch {
    const q = url.indexOf("?");
    const h = url.indexOf("#");
    const end = Math.min(q === -1 ? url.length : q, h === -1 ? url.length : h);
    path = url.slice(0, end);
  }
  return compilePathGlob(pattern).test(path);
}

/**
 * Does this host match the pattern?
 *
 *   "*"             → any host
 *   "*.example.com" → example.com OR any subdomain of it
 *   "blog.*.com"    → `*` inside a label is a non-dot run
 *   otherwise       → exact match
 *
 * The `*.` form meaning SELF-OR-SUBDOMAIN is the convention already
 * load-bearing in `widget-api/src/server/allowed-domains.ts`, mirrored in
 * `widget/src/iframe.ts` and the launch-config validator. It is NOT what a
 * generic glob would do — a generic `*.example.com` excludes the apex — and a
 * tenant who writes `*.site.com` overwhelmingly means "the site". Matching the
 * established convention beats matching glob purity.
 *
 * Deliberately a fifth implementation rather than a shared refactor: the other
 * three gate the DOMAIN ALLOWLIST, which is a security boundary. Unifying them
 * is worth doing, but not in the same change that ships a behavioural feature.
 *
 * Case-insensitive; any port is stripped before comparison.
 */
export function matchHost(host: string, pattern: string): boolean {
  if (pattern === "*") return true;
  const h = host.toLowerCase().split(":")[0] ?? "";
  const p = pattern.toLowerCase().trim();
  if (!h || !p) return false;

  if (p.startsWith("*.")) {
    const base = p.slice(2);
    if (!base) return false;
    // Self or any subdomain. If the remainder is itself a glob, fall through
    // to the regex path so "*.example.*" still behaves.
    if (!base.includes("*")) return h === base || h.endsWith(`.${base}`);
  }

  if (!p.includes("*")) return h === p;
  // `*` inside a label matches a run of non-dot characters.
  const body = escapeRegex(p).replace(/\\?\*/g, "[^.]*");
  return new RegExp(`^${body}$`, "i").test(h);
}
